import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Radio,
  InputNumber,
  Tag
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { TextArea } = Input;

interface AnswerConfigProps {
  testId: string;
  questionCount: number;
  onComplete?: () => void;
}

interface Question {
  id?: string;
  test_id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'fill_blank';
  options: string[];
  correct_answer: string;
  points: number;
  knowledge_points: string;
}

const AnswerConfig: React.FC<AnswerConfigProps> = ({ testId, questionCount, onComplete }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // Initialize questions
  useEffect(() => {
    initializeQuestions();
  }, [testId, questionCount]);

  const initializeQuestions = () => {
    const newQuestions: Question[] = [];
    for (let i = 1; i <= questionCount; i++) {
      newQuestions.push({
        test_id: testId,
        question_text: `Question ${i}`,
        question_type: 'multiple_choice',
        options: ['A', 'B', 'C', 'D'],
        correct_answer: '',
        points: Math.floor(100 / questionCount),
        knowledge_points: ''
      });
    }
    setQuestions(newQuestions);
  };

  // Save answer configuration
  const handleSaveAnswers = async () => {
    try {
      setLoading(true);
      
      // Delete existing questions
      await axios.delete(`/api/questions/test/${testId}`);
      
      // Create new questions in batch
      for (const question of questions) {
        await axios.post('/api/questions', {
          ...question,
          options: question.options,
          knowledge_points: question.knowledge_points ? [question.knowledge_points] : []
        });
      }
      
      message.success('Answer configuration saved!');
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      message.error('Failed to save answer configuration');
    } finally {
      setLoading(false);
    }
  };

  // Update a question field
  const updateQuestion = (index: number, field: keyof Question, value: any) => {
    const newQuestions = [...questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuestions(newQuestions);
  };

  // Auto-assign points evenly
  const handleAutoAssignPoints = () => {
    const totalPoints = 100;
    const pointsPerQuestion = Math.floor(totalPoints / questionCount);
    const remainder = totalPoints % questionCount;
    
    const newQuestions = questions.map((q, index) => ({
      ...q,
      points: pointsPerQuestion + (index < remainder ? 1 : 0)
    }));
    setQuestions(newQuestions);
    message.success(`Points auto-assigned: ${pointsPerQuestion} pts/question`);
  };

  return (
    <div>
      <Card 
        title="Answer Configuration" 
        extra={
          <Space>
            <Button onClick={handleAutoAssignPoints}>
              Auto-assign Points
            </Button>
            <Button type="primary" onClick={handleSaveAnswers} loading={loading}>
              Save Configuration
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: '20px', padding: '15px', background: '#f0f8ff', borderRadius: '6px' }}>
          <div style={{ color: '#1890ff', fontWeight: 'bold', marginBottom: '10px' }}>
            📋 Instructions
          </div>
          <div style={{ color: '#666', lineHeight: '1.6' }}>
            <p>• Students will view the original document you uploaded to answer questions</p>
            <p>• Configure the correct answer for each question based on the document content</p>
            <p>• Multiple choice: select the correct option (A, B, C, D)</p>
            <p>• True/False: select "True" or "False"</p>
            <p>• Fill in the blank: enter the correct answer</p>
          </div>
        </div>

        {questions.map((question, index) => (
          <Card 
            key={index}
            size="small" 
            style={{ marginBottom: '15px' }}
            title={`Question ${index + 1}`}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Question type */}
              <Form.Item label="Question Type">
                <Select
                  value={question.question_type}
                  onChange={(value) => updateQuestion(index, 'question_type', value)}
                  style={{ width: '200px' }}
                >
                  <Option value="multiple_choice">Multiple Choice</Option>
                  <Option value="true_false">True / False</Option>
                  <Option value="fill_blank">Fill in the Blank</Option>
                </Select>
              </Form.Item>

              {/* Multiple choice options */}
              {question.question_type === 'multiple_choice' && (
                <Form.Item label="Correct Answer">
                  <Radio.Group
                    value={question.correct_answer}
                    onChange={(e) => updateQuestion(index, 'correct_answer', e.target.value)}
                  >
                    {question.options.map((option, optIndex) => (
                      <Radio key={optIndex} value={option}>
                        {option}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              )}

              {/* True/False options */}
              {question.question_type === 'true_false' && (
                <Form.Item label="Correct Answer">
                  <Radio.Group
                    value={question.correct_answer}
                    onChange={(e) => updateQuestion(index, 'correct_answer', e.target.value)}
                  >
                    <Radio value="true">True</Radio>
                    <Radio value="false">False</Radio>
                  </Radio.Group>
                </Form.Item>
              )}

              {/* Fill-in-the-blank answer */}
              {question.question_type === 'fill_blank' && (
                <Form.Item label="Correct Answer">
                  <Input
                    value={question.correct_answer}
                    onChange={(e) => updateQuestion(index, 'correct_answer', e.target.value)}
                    placeholder="Enter correct answer"
                    style={{ width: '300px' }}
                  />
                </Form.Item>
              )}

              {/* Points */}
              <Form.Item label="Points">
                <InputNumber
                  value={question.points}
                  onChange={(value) => updateQuestion(index, 'points', value || 1)}
                  min={1}
                  max={100}
                  style={{ width: '100px' }}
                />
              </Form.Item>

              {/* Knowledge tag */}
              <Form.Item label="Knowledge Tag">
                <Input
                  value={question.knowledge_points}
                  onChange={(e) => updateQuestion(index, 'knowledge_points', e.target.value)}
                  placeholder="Enter knowledge tag (optional)"
                  style={{ width: '300px' }}
                />
              </Form.Item>
            </Space>
          </Card>
        ))}

        {questions.length > 0 && (
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            background: '#f6ffed', 
            borderRadius: '6px',
            border: '1px solid #b7eb8f',
            textAlign: 'center'
          }}>
            <div style={{ color: '#52c41a', fontWeight: 'bold', marginBottom: '10px' }}>
              ✅ Configuration Complete
            </div>
            <div style={{ color: '#666', marginBottom: '15px' }}>
              {questions.length} questions configured, total {questions.reduce((sum, q) => sum + (q.points || 0), 0)} points
            </div>
            <Space>
              <Button type="primary" onClick={handleSaveAnswers} loading={loading}>
                Save Configuration
              </Button>
              {onComplete && (
                <Button onClick={onComplete}>
                  Done
                </Button>
              )}
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AnswerConfig;
